import type { AzureCosts, OverviewPart } from '../../../services/adminUnits';
import { formatMoney, periodLabel } from './unitCosts';
import { formatClock } from './unitsFormat';

interface Column {
  heading: string;
  costs: AzureCosts | null;
}

const cellStyle = { padding: '7px 12px', textAlign: 'right' as const, whiteSpace: 'nowrap' as const };

const cost = (column: Column, group: string) => {
  const found = column.costs?.groups.find((candidate) => candidate.group === group);
  return found ? formatMoney(found.cost, column.costs!.currency) : '–';
};

function OtherServices({ columns }: { columns: Column[] }) {
  const names = [...new Set(columns.flatMap((column) => column.costs?.groups.find((group) => group.group === 'Other')?.azureServices ?? []))];
  return names.length > 0 ? <div className="muted" style={{ fontSize: 11.5, whiteSpace: 'normal' }}>{names.join(', ')}</div> : null;
}

/** Azure's costs of the whole subscription by service, last month beside this month so far. */
export default function ActualCosts({ lastMonth, monthToDate }: { lastMonth: OverviewPart<AzureCosts>; monthToDate: OverviewPart<AzureCosts> }) {
  const columns: Column[] = [
    { heading: lastMonth.value ? periodLabel(lastMonth.value, true) : 'Last month', costs: lastMonth.value },
    { heading: monthToDate.value ? `${periodLabel(monthToDate.value, false)}, so far` : 'This month', costs: monthToDate.value },
  ];
  const groups = (lastMonth.value ?? monthToDate.value)?.groups.map((group) => group.group) ?? [];
  const readAt = monthToDate.value?.readAt ?? lastMonth.value?.readAt;
  if (groups.length === 0) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div className="muted" style={{ fontSize: 12 }}>Azure, whole subscription</div>
      <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
        <table aria-label="Azure costs by service" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr className="muted" style={{ fontSize: 12 }}>
              <th style={{ ...cellStyle, textAlign: 'left' }}>Service</th>
              {columns.map((column) => (
                <th key={column.heading} style={cellStyle}>{column.heading}</th>
              ))}
            </tr>
          </thead>
          <tbody className="tnum">
            {groups.map((group) => (
              <tr key={group} style={{ borderTop: '1px solid var(--line)' }}>
                <td style={{ ...cellStyle, textAlign: 'left' }}>
                  {group}
                  {group === 'Other' && <OtherServices columns={columns} />}
                </td>
                {columns.map((column) => (
                  <td key={column.heading} style={cellStyle}>{cost(column, group)}</td>
                ))}
              </tr>
            ))}
            <tr style={{ borderTop: '1px solid var(--line-3)', fontWeight: 600 }}>
              <td style={{ ...cellStyle, textAlign: 'left' }}>Total</td>
              {columns.map((column) => (
                <td key={column.heading} style={cellStyle}>
                  {column.costs ? formatMoney(column.costs.total, column.costs.currency) : '–'}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      {readAt && <div className="muted" style={{ fontSize: 12 }}>Read {formatClock(readAt)}; Azure lags up to a day.</div>}
    </div>
  );
}
